import { FC, useMemo, useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import { Stack, Box, Theme } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import { UserEventMessage } from '@/lib/models/user-event';
import {
  buildToolPairInfoByToolUseId,
  getToolUseIdFromMessage,
  ToolUsePair,
} from '@/lib/models/user-event/tools';
import InlineToast from '@/components/toasts/inline';
import Message from './message';
import { createUseStyles } from 'react-jss';
import useAppData from '@/hooks/use-app-data.hook';
import { AppDataQueryKey } from '@/lib/query-client';
import { PublicServiceMeta } from '@/lib/models/public-service/public-service.model';
import { getUserEventPublicService } from '@/lib/services/public-service.service';
import PartialConversationHeader from './header/partial-conversation';
import FullConversationHeader from './header/full-conversation';
import Text from '../text';
import Loader from '../loader';
import { lighten } from '@mui/material/styles';
import { ConversationInfiniteScrollProps, getMessageGroupKey, MessageGroup } from './helpers';

interface Props extends ConversationInfiniteScrollProps {
  userEvent: UserEventModel;
  variant: 'conversation' | 'user-event';
  focusedRequestId?: string;
  anchorActivityId?: string;
  emptyMessage?: string;
  isLoading?: boolean;
}

const LOAD_MORE_THRESHOLD = 3;
const ESTIMATED_GROUP_HEIGHT = 120;

const useStyles = createUseStyles<string, { isFullConversation: boolean; isVirtualized: boolean }>(
  (theme: Theme) => ({
    container: {
      width: '100%',
    },
    containerVirtualized: {
      width: '100%',
      height: '100%',
      minHeight: 0,
      paddingBottom: 24,
      boxSizing: 'border-box',
    },
    messagesContainer: {
      gap: 16,
      marginTop: ({ isFullConversation, isVirtualized }) =>
        isVirtualized ? 0 : isFullConversation ? 48 : 24,
      marginRight: 16,
    },
    scrollViewport: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    },
    scrollContainer: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      paddingTop: 24,
      height: 'calc(100% - 24px)',
    },
    virtualListContainer: {
      width: '100%',
      position: 'relative',
    },
    virtualItem: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
    },
    bottomLoader: {
      display: 'flex',
      justifyContent: 'center',
      padding: 16,
    },
    topLoader: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1,
      display: 'flex',
      justifyContent: 'center',
      padding: 8,
      pointerEvents: 'none',
    },
    infoText: {
      textAlign: 'center',
    },
    messageGroup: {
      backgroundColor: lighten(theme.palette.primary.main, 0.97),
      borderRadius: 8,
      gap: 16,
    },
  })
);

const Conversation: FC<Props> = ({
  userEvent,
  variant,
  focusedRequestId,
  anchorActivityId,
  emptyMessage = 'No conversations',
  isLoading = false,
  hasMoreMessages = false,
  isFetchingMoreMessages = false,
  onLoadMoreMessages,
  hasOlderMessages = false,
  isFetchingOlderMessages = false,
  onLoadOlderMessages,
}) => {
  const isVirtualized = !!onLoadMoreMessages;
  const isFullConversation = variant === 'conversation';
  const styles = useStyles({ isFullConversation, isVirtualized });
  const { requestId, messages, conversationId, spId } = userEvent;
  const { conversation } = messages;
  const effectiveFocusedRequestId = focusedRequestId ?? requestId;
  const psQuery = useAppData<PublicServiceMeta[]>(AppDataQueryKey.PublicServices);
  const { data: publicServices = [] } = psQuery;

  const conversationHasLength = isFullConversation && conversation.length > 2;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [openPhase, setOpenPhase] = useState<'initial' | 'loadingOlder' | 'ready'>('initial');
  const lastAppendedLengthRef = useRef(0);
  const prependAnchorRef = useRef<{ key: string; offset: number; atLength: number } | null>(null);

  const publicService = getUserEventPublicService(userEvent, publicServices);
  const hasRespIntercept =
    publicService?.supportLevels.includes('Response intercept') || publicService?.isF5WAIS;

  const messageGroups = useMemo<MessageGroup[]>(() => {
    if (variant !== 'conversation' || !conversationHasLength) {
      return conversation.map((message) => ({
        key: getMessageGroupKey(message),
        messages: [message],
        isFocused: false,
      }));
    }

    const groups: MessageGroup[] = [];
    let currentGroup: UserEventMessage[] = [];
    let currentRequestId: string | undefined = undefined;

    const pushCurrentGroup = () => {
      groups.push({
        key: getMessageGroupKey(currentGroup[0]),
        messages: currentGroup,
        isFocused: currentRequestId === effectiveFocusedRequestId,
      });
    };

    conversation.forEach((message) => {
      if (message.requestId === currentRequestId) {
        currentGroup.push(message);
      } else {
        if (currentGroup.length) {
          pushCurrentGroup();
        }
        currentGroup = [message];
        currentRequestId = message.requestId;
      }
    });

    if (currentGroup.length) {
      pushCurrentGroup();
    }

    return groups;
  }, [conversation, variant, conversationHasLength, effectiveFocusedRequestId]);

  const toolPairByToolUseId = useMemo(
    () => buildToolPairInfoByToolUseId(conversation),
    [conversation]
  );

  const getMessageToolUsePairData = useCallback(
    (message: UserEventMessage): ToolUsePair | undefined => {
      const toolUseId = getToolUseIdFromMessage(message);
      if (!toolUseId) {
        return undefined;
      }
      return toolPairByToolUseId.get(toolUseId);
    },
    [toolPairByToolUseId]
  );

  const getItemKey = useCallback(
    (index: number) => messageGroups[index]?.key ?? index,
    [messageGroups]
  );

  const virtualizer = useVirtualizer({
    count: isVirtualized && !isLoading ? messageGroups.length : 0,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_GROUP_HEIGHT,
    overscan: 5,
    getItemKey,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const renderMessageGroup = useCallback(
    (group: MessageGroup) => {
      const groupMessagesEl = group.messages.map((message, messageIndex) => {
        const toolUsePair = getMessageToolUsePairData(message);
        return (
          <Message
            key={`msg-${group.key}-${messageIndex}`}
            message={message}
            userEvent={userEvent}
            isInFocusedGroup={group.isFocused}
            toolUsePair={toolUsePair}
          />
        );
      });

      if (group.isFocused) {
        return (
          <Stack className={styles.messageGroup} p={1} mr={1} mb={2}>
            {groupMessagesEl}
          </Stack>
        );
      }

      if (isVirtualized) {
        return <Stack gap={2}>{groupMessagesEl}</Stack>;
      }

      return groupMessagesEl;
    },
    [getMessageToolUsePairData, isVirtualized, styles.messageGroup, userEvent]
  );

  const scrollToFocusedGroup = useCallback(() => {
    const focusedIndex = messageGroups.findIndex(({ isFocused }) => isFocused);
    if (focusedIndex < 0) {
      return;
    }

    virtualizer.scrollToIndex(focusedIndex, { align: 'start', behavior: 'smooth' });
  }, [messageGroups, virtualizer]);

  const canScrollToRequestMessage = isFullConversation && conversation.length > 2;

  useLayoutEffect(() => {
    const anchor = prependAnchorRef.current;
    if (!anchor || messageGroups.length === anchor.atLength) {
      return;
    }

    prependAnchorRef.current = null;

    const anchorIndex = messageGroups.findIndex(({ key }) => key === anchor.key);
    if (anchorIndex < 0) {
      return;
    }

    const anchorOffset = virtualizer.getOffsetForIndex(anchorIndex, 'start')?.[0];
    if (anchorOffset === undefined) {
      return;
    }

    virtualizer.scrollToOffset(anchorOffset + anchor.offset, { align: 'start' });
  }, [messageGroups, virtualizer]);

  useEffect(() => {
    if (!isVirtualized || isLoading || !messageGroups.length || openPhase === 'ready') {
      return;
    }

    const settleOnAnchor = () => {
      setOpenPhase('ready');
      const focusedIndex = messageGroups.findIndex(({ isFocused }) => isFocused);
      if (focusedIndex > 0) {
        virtualizer.scrollToIndex(focusedIndex, { align: 'start' });
      }
    };

    if (openPhase === 'initial') {
      if (hasOlderMessages && onLoadOlderMessages) {
        setOpenPhase('loadingOlder');
        onLoadOlderMessages();
        return;
      }
      settleOnAnchor();
      return;
    }

    if (!isFetchingOlderMessages) {
      settleOnAnchor();
    }
  }, [
    isVirtualized,
    isLoading,
    messageGroups,
    virtualizer,
    openPhase,
    hasOlderMessages,
    isFetchingOlderMessages,
    onLoadOlderMessages,
  ]);

  useEffect(() => {
    if (
      !isVirtualized ||
      !hasMoreMessages ||
      isFetchingMoreMessages ||
      !onLoadMoreMessages ||
      !messageGroups.length
    ) {
      return;
    }

    if (!virtualItems.length) {
      return;
    }

    const lastItem = virtualItems[virtualItems.length - 1];
    if (lastItem.index >= messageGroups.length - LOAD_MORE_THRESHOLD) {
      if (lastAppendedLengthRef.current === messageGroups.length) {
        return;
      }
      lastAppendedLengthRef.current = messageGroups.length;
      onLoadMoreMessages();
    }
  }, [
    isVirtualized,
    hasMoreMessages,
    isFetchingMoreMessages,
    onLoadMoreMessages,
    messageGroups.length,
    virtualizer,
    virtualItems,
  ]);

  useEffect(() => {
    if (
      !isVirtualized ||
      !hasOlderMessages ||
      isFetchingOlderMessages ||
      !onLoadOlderMessages ||
      !messageGroups.length ||
      prependAnchorRef.current ||
      openPhase !== 'ready'
    ) {
      return;
    }

    const scrollEl = scrollRef.current;
    if (!scrollEl) {
      return;
    }

    // Read live: a prepend repositions the list in a layout effect, so this
    // render's `virtualItems` still reads as scrolled to the top and would chain.
    const { scrollTop } = scrollEl;
    const topItem = virtualizer.getVirtualItemForOffset(scrollTop);
    if (!topItem || topItem.index > LOAD_MORE_THRESHOLD) {
      return;
    }

    prependAnchorRef.current = {
      key: String(topItem.key),
      offset: scrollTop - topItem.start,
      atLength: messageGroups.length,
    };

    onLoadOlderMessages();
  }, [
    isVirtualized,
    hasOlderMessages,
    isFetchingOlderMessages,
    onLoadOlderMessages,
    openPhase,
    messageGroups.length,
    virtualizer,
    virtualItems,
  ]);

  if (!conversation.length && !isLoading) {
    return <InlineToast message={emptyMessage} level="info" />;
  }

  const interceptNotice = !hasRespIntercept && (
    <Text size="small" color="grey" className={styles.infoText}>
      Only request intercept is supported for {publicService?.name}
    </Text>
  );

  const headerEl = isFullConversation ? (
    <FullConversationHeader
      resourceId={conversationId}
      onJumpToUserEvent={canScrollToRequestMessage ? scrollToFocusedGroup : undefined}
      fullWidth
    />
  ) : (
    <PartialConversationHeader
      resourceId={spId}
      openConversationId={conversationId}
      focusedRequestId={requestId}
      anchorActivityId={anchorActivityId}
    />
  );

  if (isVirtualized) {
    return (
      <Stack className={styles.containerVirtualized}>
        {headerEl}
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {interceptNotice}
            <Box className={styles.scrollViewport}>
              {isFetchingOlderMessages && (
                <Box className={styles.topLoader}>
                  <Loader />
                </Box>
              )}
              <Box ref={scrollRef} className={styles.scrollContainer}>
                <Box className={styles.virtualListContainer} height={virtualizer.getTotalSize()}>
                  {virtualItems.map((virtualRow) => {
                    const group = messageGroups[virtualRow.index];
                    return (
                      <Box
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        ref={virtualizer.measureElement}
                        className={styles.virtualItem}
                        style={{ transform: `translateY(${virtualRow.start}px)` }}
                      >
                        {renderMessageGroup(group)}
                      </Box>
                    );
                  })}
                </Box>
                {isFetchingMoreMessages && (
                  <Box className={styles.bottomLoader}>
                    <Loader />
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}
      </Stack>
    );
  }

  return (
    <Stack className={styles.container}>
      {headerEl}
      {isLoading ? (
        <Loader />
      ) : (
        <Stack className={styles.messagesContainer}>
          {interceptNotice}
          {messageGroups.map((group, groupIndex) => (
            <Box key={groupIndex}>{renderMessageGroup(group)}</Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default Conversation;
